import type { AuthProvider } from "@refinedev/core";

import { ApiError } from "../api";
import { authClient, authErrorMessage } from "../auth-client";
import { clearMe, loadMe } from "../session";

type LoginParams = { email: string; password: string } | { code: string };

const failure = (message: string) => ({
  success: false,
  error: { name: "Giriş yapılamadı", message },
});

// Sign-in has two steps: password, then the authenticator code (docs/FRONTEND_ROUTES.md →
// Office Routes). Staff who have not set up the code yet are sent to /setup-two-factor.
export const authProvider: AuthProvider = {
  login: async (params: LoginParams) => {
    clearMe();
    if ("code" in params) {
      const { error } = await authClient.twoFactor.verifyTotp({ code: params.code });
      if (error) return failure(authErrorMessage(error));
      return { success: true, redirectTo: "/" };
    }
    const { data, error } = await authClient.signIn.email({
      email: params.email,
      password: params.password,
    });
    if (error) return failure(authErrorMessage(error));
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) {
      return { success: true, redirectTo: "/login/two-factor" };
    }
    return { success: true, redirectTo: "/setup-two-factor" };
  },

  logout: async () => {
    await authClient.signOut();
    clearMe();
    return { success: true, redirectTo: "/login" };
  },

  check: async () => {
    const me = await loadMe();
    return me ? { authenticated: true } : { authenticated: false, redirectTo: "/login" };
  },

  onError: async (error: unknown) => {
    if (error instanceof ApiError && error.statusCode === 401) {
      clearMe();
      return { logout: true, redirectTo: "/login" };
    }
    if (error instanceof ApiError && error.code === "TWO_FACTOR_REQUIRED") {
      clearMe();
      return { redirectTo: "/setup-two-factor" };
    }
    return {};
  },

  getIdentity: async () => loadMe(),

  getPermissions: async () => (await loadMe())?.permissions ?? [],
};
